import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { BarChart3, Play, Plus, SkipForward, Trash2, Trophy } from "lucide-react"
import { useState } from "react"
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { toast } from "sonner"
import { Badge } from "../components/ui/badge"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Progress } from "../components/ui/progress"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet"
import { Skeleton } from "../components/ui/skeleton"
import { Textarea } from "../components/ui/textarea"
import { apiFetch } from "../utils/apiFetch"
import { type TProgram, type TProgramReport, type TProgramStatus, type TWorkoutTemplate } from "../utils/workoutUtils"

export const Route = createFileRoute("/_app/programs")({
  component: ProgramsPage,
})

function ProgramsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showCreate, setShowCreate] = useState(false)
  const [reportProgramId, setReportProgramId] = useState<string | null>(null)

  const { data: programs, isLoading } = useQuery({
    queryKey: ["programs"],
    queryFn: () => apiFetch<TProgram[]>("/v1/programs"),
  })

  const { data: activeProgram } = useQuery({
    queryKey: ["programs", "active"],
    queryFn: () => apiFetch<TProgram>("/v1/programs/active"),
  })

  const { data: reportData } = useQuery({
    queryKey: ["programs", reportProgramId, "report"],
    queryFn: () => apiFetch<TProgramReport>(`/v1/programs/${reportProgramId}/report`),
    enabled: !!reportProgramId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/programs/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      toast.success("Program deleted")
    },
    onError: () => toast.error("Failed to delete program"),
  })

  const startMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/programs/${id}/start`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      toast.success("Program started!")
    },
    onError: () => toast.error("Failed to start program"),
  })

  const advanceMutation = useMutation({
    mutationFn: (data: { programId: string; status: "completed" | "skipped"; workoutId?: string }) =>
      apiFetch("/v1/programs/advance", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      toast.success("Program advanced")
    },
    onError: () => toast.error("Failed to advance program"),
  })

  const completeMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/programs/${id}/complete`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      toast.success("Program completed!")
    },
    onError: () => toast.error("Failed to complete program"),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/programs/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      toast.success("Program cancelled")
    },
    onError: () => toast.error("Failed to cancel program"),
  })

  const activeProgramData = activeProgram
  const currentTemplate = activeProgramData?.workoutTemplates.find(
    (wt) => wt.sortOrder === activeProgramData.currentWorkoutIndex
  )?.template

  const totalWorkouts = activeProgramData
    ? activeProgramData.workoutTemplates.length * activeProgramData.durationWeeks
    : 0
  const completedWorkouts = activeProgramData
    ? (activeProgramData.currentWeek - 1) * activeProgramData.workoutTemplates.length +
      activeProgramData.currentWorkoutIndex
    : 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-bold text-2xl">Programs</h1>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Program
        </Button>
      </div>

      {activeProgramData && (
        <Card className="border-primary/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-lg">{activeProgramData.name}</CardTitle>
              <p className="text-muted-foreground text-sm">
                Week {activeProgramData.currentWeek} of {activeProgramData.durationWeeks}
                {currentTemplate && <> — Next: {currentTemplate.name}</>}
              </p>
            </div>
            <Badge variant="default">Active</Badge>
          </CardHeader>
          <CardContent>
            <Progress value={(completedWorkouts / Math.max(totalWorkouts, 1)) * 100} className="mb-4" />
            <div className="flex flex-wrap gap-2">
              {currentTemplate && (
                <Button
                  size="sm"
                  onClick={() => {
                    navigate({ to: "/workout", search: { templateId: currentTemplate.workoutTemplateId } })
                  }}
                >
                  <Play className="mr-1 h-4 w-4" />
                  Start Workout
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => advanceMutation.mutate({ programId: activeProgramData.programId, status: "skipped" })}
              >
                <SkipForward className="mr-1 h-4 w-4" />
                Skip
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReportProgramId(activeProgramData.programId)}>
                <BarChart3 className="mr-1 h-4 w-4" />
                Report
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (confirm("Cancel this program? You can restart it later.")) {
                    cancelMutation.mutate(activeProgramData.programId)
                  }
                }}
              >
                Cancel Program
              </Button>
              {activeProgramData.currentWeek >= activeProgramData.durationWeeks &&
                activeProgramData.currentWorkoutIndex >= activeProgramData.workoutTemplates.length - 1 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => completeMutation.mutate(activeProgramData.programId)}
                  >
                    <Trophy className="mr-1 h-4 w-4" />
                    Complete Program
                  </Button>
                )}
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : programs && programs.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {programs.map((program) => (
            <ProgramCard
              key={program.programId}
              program={program}
              onStart={() => startMutation.mutate(program.programId)}
              onDelete={() => {
                if (confirm(`Delete "${program.name}"? This cannot be undone.`)) {
                  deleteMutation.mutate(program.programId)
                }
              }}
              onReport={() => setReportProgramId(program.programId)}
            />
          ))}
        </div>
      ) : (
        <div className="py-12 text-center text-muted-foreground">
          <p className="mb-4 text-lg">No programs yet.</p>
          <Button variant="outline" onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create your first program
          </Button>
        </div>
      )}

      <CreateProgramDialog open={showCreate} onOpenChange={setShowCreate} />

      <ProgramReportSheet
        open={!!reportProgramId}
        onOpenChange={(open) => !open && setReportProgramId(null)}
        report={reportData}
      />
    </div>
  )
}

function ProgramCard({
  program,
  onStart,
  onDelete,
  onReport,
}: {
  program: TProgram
  onStart: () => void
  onDelete: () => void
  onReport: () => void
}) {
  const statusColor: Record<TProgramStatus, string> = {
    inactive: "bg-muted text-muted-foreground",
    active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
    completed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100",
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-base">{program.name}</CardTitle>
          <p className="text-muted-foreground text-sm">
            {program.durationWeeks} week{program.durationWeeks > 1 ? "s" : ""}
            {program.workoutTemplates.length > 0 && (
              <>
                {" "}
                &middot; {program.workoutTemplates.length} workout{program.workoutTemplates.length > 1 ? "s" : ""} per
                week
              </>
            )}
          </p>
        </div>
        <Badge className={statusColor[program.status]} variant="outline">
          {program.status}
        </Badge>
      </CardHeader>
      <CardContent>
        {program.status === "active" && (
          <div className="mb-3">
            <p className="text-sm">
              Week {program.currentWeek} of {program.durationWeeks}
            </p>
            <Progress
              value={
                (((program.currentWeek - 1) * program.workoutTemplates.length + program.currentWorkoutIndex) /
                  (program.workoutTemplates.length * program.durationWeeks)) *
                100
              }
              className="mt-1 h-2"
            />
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {program.status === "inactive" && (
            <Button size="sm" onClick={onStart}>
              <Play className="mr-1 h-4 w-4" />
              Start
            </Button>
          )}
          <Button size="sm" variant="outline" asChild>
            <Link to="/program/$id" params={{ id: program.programId }}>
              View
            </Link>
          </Button>
          <Button size="sm" variant="outline" onClick={onReport}>
            <BarChart3 className="mr-1 h-4 w-4" />
            Report
          </Button>
          {program.status !== "active" && (
            <Button size="sm" variant="ghost" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function CreateProgramDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState("")
  const [durationWeeks, setDurationWeeks] = useState(4)
  const [notes, setNotes] = useState("")
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([])

  const { data: templates } = useQuery({
    queryKey: ["workoutTemplates"],
    queryFn: () => apiFetch<TWorkoutTemplate[]>("/v1/workoutTemplates"),
  })

  const createMutation = useMutation({
    mutationFn: (data: { name: string; durationWeeks: number; notes?: string; templateIds: string[] }) =>
      apiFetch("/v1/programs", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] })
      toast.success("Program created!")
      reset()
      onOpenChange(false)
    },
    onError: () => toast.error("Failed to create program"),
  })

  const reset = () => {
    setName("")
    setDurationWeeks(4)
    setNotes("")
    setSelectedTemplateIds([])
  }

  const toggleTemplate = (id: string) => {
    setSelectedTemplateIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset()
        }
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Program</DialogTitle>
          <DialogDescription>
            Name your program, set the duration, and select the templates to cycle through.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 5/3/1 Main Lift"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="weeks">Duration (weeks)</Label>
            <Input
              id="weeks"
              type="number"
              min={1}
              max={52}
              value={durationWeeks}
              onChange={(e) => setDurationWeeks(Math.max(1, Number(e.target.value)))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this program"
            />
          </div>
          <div className="space-y-2">
            <Label>Workout Templates (in order)</Label>
            {templates && templates.length > 0 ? (
              <div className="space-y-1">
                {templates.map((t) => (
                  <label
                    key={t.workoutTemplateId}
                    className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm hover:bg-accent"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTemplateIds.includes(t.workoutTemplateId)}
                      onChange={() => toggleTemplate(t.workoutTemplateId)}
                      className="h-4 w-4"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{t.name}</p>
                      {t.jsonBlob?.exercises && (
                        <p className="text-muted-foreground text-xs">
                          {t.jsonBlob.exercises.length} exercise{t.jsonBlob.exercises.length !== 1 ? "s" : ""}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                No templates available.{" "}
                <Link to="/templates" className="underline">
                  Create some templates first
                </Link>
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={!name || selectedTemplateIds.length === 0}
            onClick={() =>
              createMutation.mutate({
                name,
                durationWeeks,
                notes: notes || undefined,
                templateIds: selectedTemplateIds,
              })
            }
          >
            Create Program
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ProgramReportSheet({
  open,
  onOpenChange,
  report,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  report: TProgramReport | undefined
}) {
  if (!report) {
    return null
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{report.program.name} — Report</SheetTitle>
          <SheetDescription>
            {report.program.status === "completed" ? "Program completed" : "Program in progress"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-8">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border p-3 text-center">
              <p className="font-bold text-2xl">{report.stats.totalWorkouts}</p>
              <p className="text-muted-foreground text-xs">Total Workouts</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="font-bold text-2xl">{report.stats.completionRate}%</p>
              <p className="text-muted-foreground text-xs">Completion Rate</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="font-bold text-2xl">{report.stats.completed}</p>
              <p className="text-muted-foreground text-xs">Completed</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="font-bold text-2xl">{report.stats.weeksCompleted}</p>
              <p className="text-muted-foreground text-xs">Weeks Done</p>
            </div>
          </div>

          {report.weeklyVolume.length > 0 && (
            <div>
              <h3 className="mb-2 font-semibold text-sm">Weekly Volume</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={report.weeklyVolume}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="totalVolume" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {report.exerciseProgression.length > 0 && (
            <div>
              <h3 className="mb-2 font-semibold text-sm">Exercise Progression</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  {report.exerciseProgression.slice(0, 5).map((ex, i) => (
                    <Line
                      key={ex.exerciseName}
                      data={ex.data}
                      type="monotone"
                      dataKey="maxWeight"
                      name={ex.exerciseName}
                      stroke={`hsl(${i * 60}, 60%, 50%)`}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              {report.exerciseProgression.length > 5 && (
                <p className="mt-1 text-muted-foreground text-xs">
                  Showing first 5 exercises ({report.exerciseProgression.length} total)
                </p>
              )}
            </div>
          )}

          {report.templateBreakdown.length > 0 && (
            <div>
              <h3 className="mb-2 font-semibold text-sm">Template Breakdown</h3>
              <div className="space-y-2">
                {report.templateBreakdown.map((t) => (
                  <div key={t.name} className="flex items-center justify-between rounded-md border p-2 text-sm">
                    <span className="font-medium">{t.name}</span>
                    <span className="text-muted-foreground">
                      {t.completed}/{t.total} completed
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.weeklyVolume.length === 0 &&
            report.exerciseProgression.length === 0 &&
            report.templateBreakdown.length === 0 && (
              <p className="text-center text-muted-foreground text-sm">
                No workout data yet. Start the program and log workouts to see progress.
              </p>
            )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
